import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import type { Transport } from "@tdelivr/api-client";
import { services } from "@tdelivr/api-client";
// The row shape IAM-03 publishes, imported rather than restated.
import type { AuthorizationPolicy } from "@tdelivr/api-client/generated/identity/authorization-policy.js";

import { boundedNote, PER_FETCH, type BoardRow } from "../board.ts";
import { matchesEffect, policyRow, POLICY_EFFECTS } from "../roles.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { RecordsBoard } from "./RecordsBoard.tsx";

/**
 * Roles and permissions: the custom roles this tenant has written.
 *
 * Built to `17 WP16 Tenant Admin.dc.html`, page `roles`, against IAM-03
 * `GET /v1/tenants/{tenantId}/policies` — an operation that did not exist until ADR 0080, which is
 * what this page was recorded as blocked on.
 *
 * **The blocker was accurate and it was the only one.** "IAM-03 publishes two policy writes and no
 * policy read. A roles page cannot show a role that has already been created, so it would be a form
 * with no list behind it." The read is published now, bounded by the tenant on the token, and the
 * form is still refused — for a different and smaller reason than the missing list.
 *
 * **Three things the design draws have no source, and `roles.ts` records each in full.** `Members` is
 * the sharpest: IAM-03 holds policies and not people, so the column carries the absence rather than
 * a count of `subjectRoles`, which is a list of role names. `Sensitive` has a real field beside it —
 * a rule's `requiredScopes` — and the design's `protected`, `two-person`, `limited` and
 * `≤ KES 2,000` are a classification and a limit that nothing publishes, so the column shows the
 * scopes and says so. All four tiles are absent, which is this console's established answer on all
 * five of its record boards rather than a new one.
 *
 * **The filter narrows rows rather than refetching**, because this listing accepts `limit` and
 * `after` and no filter at all — the opposite of the customers board, whose operation takes a
 * `status`. Worth stating on the page, because a filter that silently searches one page of a bounded
 * read is a filter that appears to find nothing.
 */

const policyApi = services.identity__authorization_policy;

export interface RolesProps {
  readonly transport: Transport;
  /**
   * The tenant whose policies to read.
   *
   * A required prop rather than a value this component reaches for. The route is addressed by
   * `tenantId` and IAM-03 refuses a path naming a tenant other than the one on the token with a
   * 403, so the only correct value is the session's own — and a component that read the session
   * itself would be deciding which tenant it acts for, which `apps/README.md` forbids.
   */
  readonly tenantId: string;
  /** Innermost last: tenant, group, page. Rendered in the page body, never in the top bar. */
  readonly crumbs?: readonly string[];
}

interface Loaded {
  readonly policies: readonly AuthorizationPolicy[];
  readonly truncated: boolean;
}

export function Roles({ transport, tenantId, crumbs }: RolesProps): ReactNode {
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [effect, setEffect] = useState("");

  const load = useCallback(async () => {
    const client = policyApi.createAuthorizationPolicyClient(transport);
    try {
      const answered = await client.getTenantsPolicies(tenantId, PER_FETCH, undefined);
      setLoaded({
        policies: answered.policies,
        truncated: typeof answered.nextAfter === "string" && answered.nextAfter.length > 0,
      });
      setFailure(undefined);
    } catch (error) {
      setFailure(stateForError(error, ["authorization:policy:read"]));
      setLoaded(undefined);
    }
  }, [transport, tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows: readonly BoardRow[] | undefined = useMemo(() => {
    if (loaded === undefined) return undefined;
    return loaded.policies
      .filter((policy) => matchesEffect(policy, effect))
      .map((policy) => policyRow(policy));
  }, [loaded, effect]);

  const bounded = boundedNote(
    loaded?.policies.length,
    loaded?.truncated ?? false,
    "roles",
    "by identifier",
  );

  return (
    <RecordsBoard
      crumbs={crumbs ?? []}
      title="Roles and permissions"
      source="IAM-03"
      /*
       * The design's sentence is replaced, and this is the one place this page's copy is not the
       * design's. "Custom roles for your team. Sensitive permissions are marked and need a second
       * person." The first clause is kept. The second is a safety claim about a control that does
       * not exist: nothing on this platform marks a permission as sensitive, and nothing requires a
       * second person for anything. An administrator who believes a payout approval needs two
       * people will write a role expecting a protection that is not there, which is the same class
       * of promise the customers board removed about masking and auditing. Q-107's shape.
       */
      description="Custom roles for your team. IAM-03 publishes each role's rules and the scopes they require, and it marks no permission as sensitive."
      columns={["Role", "Members", "Required scopes"]}
      rows={rows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={() => {
        void load();
      }}
      purpose="A role appears here as soon as one is created for this tenant."
      primary={{
        label: "Create role",
        /*
         * Drawn, and refused. `POST /v1/tenants/{tenantId}/policies` exists and this page may not
         * send it — the console is granted the read and not the write, which ADR 0080 argues is the
         * right split — and the designed form could not fill it either. Its fields are `Role name`,
         * `Description`, and three chip groups `Operations`, `Finance` and `Admin`; a policy's rule
         * requires an `id`, an `effect`, at least one `action` and at least one `resourceType`, and
         * the chips name none of those. The three groups are themselves the sensitivity grouping
         * that nothing publishes.
         */
        refusal:
          "This board holds authorization:policy:read and not :write, and the designed form's three chip groups cannot fill a rule, which requires an id, an effect, an action and a resource type. The groups are the sensitivity grouping IAM-03 does not publish.",
      }}
      filter={{
        unfiltered: "All roles",
        options: POLICY_EFFECTS,
        value: effect,
        onChange: setEffect,
      }}
      note={{
        /*
         * The design's `lock` note is replaced and keeps its kind, its glyph and its place.
         *
         * It reads "The owner role can't be removed while it is the only one." Three things in that
         * sentence have no counterpart: IAM-03 has no owner role and no notion of one, it publishes
         * no delete at all — so nothing can be removed, by anybody, which is the stronger statement
         * — and it holds no membership to be the only one of.
         */
        kind: "lock",
        glyph: "lock",
        text: "IAM-03 publishes no delete, so a role cannot be removed here — it is replaced, and its version is in the detail panel. It holds no owner role and no membership.",
      }}
      {...(bounded === undefined ? {} : { bounded })}
      footnote="Every authorization policy this tenant holds, ordered by identifier. The filter narrows these rows: this listing takes a limit and a cursor and no filter, so a role past the bound is not searched."
      exportName="roles"
      detailSubtitle="Roles and permissions · IAM-03"
      detailNote="IAM-03 publishes no delete, so a role cannot be removed here — it is replaced, and its version is in the detail panel. It holds no owner role and no membership."
    />
  );
}
